import React, { useRef, useState } from 'react';

import { useCountUp } from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import styled from 'styled-components';

import InnerHtml from 'components/InnerHtml/InnerHtml';

import { AnimatedCardI } from 'interfaces/content-stack/modular-blocks/animatedCards';

const CounterContainer = styled.div`
  gap: 0.5rem;
  flex-direction: column;
  grid-template-rows: auto auto;
  grid-template-columns: 1fr;
  grid-auto-columns: 1fr;
  align-content: center;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 0;
  padding: 2rem 1rem 1.5rem;
  display: grid;
  position: relative;
`;

const NumberDisplay = styled.div`
  display: flex;
  z-index: 10;
  justify-content: center;
  align-items: flex-end;
  display: flex;
  position: relative;
  font-family: ${({ theme }) => theme.fonts.fontFamilySecondary};
`;

const Affix = styled.div`
  align-self: flex-end;
  margin-bottom: 0.6rem;
  font-size: 3rem;
  line-height: 1;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
    font-size: 4rem;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 2.5rem;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.xs}) {
    font-size: 1.7rem;
  }
`;

const Number = styled.div`
  font-size: 6rem;
  line-height: 1.1;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
    font-size: 8rem;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 5rem;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.xs}) {
    font-size: 4rem;
  }
`;

const CardDetails = styled.div`
  text-align: center;
  font-size: 0.875rem;
  line-height: 1.3;
  position: relative;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.xs}) {
    font-size: 0.75rem;
  }
`;

const CountdownCard: React.FC<{ card: AnimatedCardI }> = ({ card }) => {
  const [displayed, setDisplayed] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(true);
  const decimal = (card.startingValue ?? 100) < 1 && (card.endingValue ?? 0) < 1;
  const prefixHasNumber = card.prefix && hasNumber(card.prefix);
  const countUpRef = useRef(null);
  const { start, reset } = useCountUp({
    ref: countUpRef,
    start: card.startingValue ?? 100,
    end: card.endingValue ?? 0,
    decimals: decimal ? 2 : 0,
    delay: 0,
    duration: 2.5,
    useEasing: false,
    onEnd: () => {
      setIsCountingDown(false);
    },
  });

  function hasNumber(text: string) {
    const regex = /\d/;
    return regex.test(text);
  }

  function onChange(isVisible: boolean) {
    if (isVisible && !displayed) {
      reset();
      start();
      setDisplayed(true);
    }
  }

  return (
    <CounterContainer>
      <NumberDisplay>
        {prefixHasNumber ? <Number>{card.prefix}</Number> : <Affix>{card.prefix}</Affix>}
        <VisibilitySensor onChange={onChange}>
          <Number ref={countUpRef}>{!isCountingDown && card?.endingValue}</Number>
        </VisibilitySensor>
        <Affix>{card.suffix}</Affix>
      </NumberDisplay>
      {card.details && (
        <CardDetails>
          <InnerHtml fontWeight='500' richText={card.details} />
        </CardDetails>
      )}
    </CounterContainer>
  );
};

export default CountdownCard;
